// src/app/ui/pages/conditions-page/condition-sheet/condition-sheet.component.ts
import {
  Component,
  OnInit,
  OnDestroy,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser, DOCUMENT } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription, EMPTY } from 'rxjs';
import {
  switchMap,
  map,
  distinctUntilChanged,
  catchError,
  tap,
} from 'rxjs/operators';

import { Condition, RawConditionData } from '@models/condition.models';
import { buildCondition } from '@models/condition-transformer';
import { ConditionService } from '@services/condition.service';
import { SectionComponent } from '@ui/pages/conditions-page/condition-sheet/sections/section.component';
import { ScrollStateService } from '@services/scroll-state.service';

@Component({
  selector: 'app-condition-sheet',
  templateUrl: './condition-sheet.component.html',
  styleUrls: ['./condition-sheet.component.scss'],
  standalone: true,
  imports: [CommonModule, SectionComponent],
})
export class ConditionSheetComponent implements OnInit, OnDestroy {
  data$!: Observable<Condition>;
  path$!: Observable<Array<{ label: string; link: any[] | null }>>;
  atTop$!: Observable<boolean>;

  private atTop = true;
  private sub?: Subscription;
  private redirected = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private conditionService: ConditionService,
    private scrollState: ScrollStateService,
    @Inject(DOCUMENT) private doc: Document,
    @Inject(PLATFORM_ID) private pid: Object,
  ) {}

  ngOnInit(): void {
    this.atTop$ = this.scrollState.atTop$;
    this.sub = this.atTop$.subscribe((v) => (this.atTop = v));

    const parent = this.route.parent ?? this.route;

    this.data$ = parent.paramMap.pipe(
      map((pm) => ({
        module: pm.get('module')?.trim() ?? '',
        slug: pm.get('slug')?.trim() ?? '',
      })),
      distinctUntilChanged(
        (a, b) => a.module === b.module && a.slug === b.slug,
      ),
      tap(({ module, slug }) => {
        // If params are missing, treat it as not-found.
        if (!module || !slug) this.goNotFound();
      }),
      switchMap(({ module, slug }) => {
        if (!module || !slug) return EMPTY;
        return this.conditionService.getConditionBySlug(module, slug);
      }),
      map((raw: RawConditionData): Condition => buildCondition(raw)),
      catchError((err: unknown) => {
        // Covers:
        // - "Entry module mismatch" (your explicit thrown error)
        // - HTTP 404s
        // - any unexpected failures
        console.error(err);
        this.goNotFound();
        return EMPTY;
      }),
    );

    this.path$ = this.data$.pipe(
      map((c) => c.path_segments ?? []),
      map((segs) =>
        segs.map((label, i) => ({
          label,
          link: ['/taxonomy', ...segs.slice(0, i + 1)],
        })),
      ),
    );
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onTitleClick(ev: Event): void {
    if (this.atTop) return;
    this.scrollToTop(ev);
  }

  scrollToTop(ev?: Event): void {
    if (!isPlatformBrowser(this.pid)) return;
    const header = (ev?.currentTarget as HTMLElement) ?? null;
    const scroller =
      this.getScrollParent(header) ??
      (this.doc.scrollingElement as HTMLElement | null);
    scroller?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private getScrollParent(el: HTMLElement | null): HTMLElement | null {
    if (!isPlatformBrowser(this.pid)) return null;
    let node: HTMLElement | null = el ? el.parentElement : null;
    while (node && node !== this.doc.body) {
      const s = getComputedStyle(node);
      if (/(auto|scroll|overlay)/.test(s.overflowY)) return node;
      node = node.parentElement;
    }
    return this.doc.scrollingElement as HTMLElement | null;
  }

  private goNotFound(): void {
    if (this.redirected) return;
    this.redirected = true;

    // Only navigate on the browser; SSR/prerender should not try to imperatively navigate here.
    if (!isPlatformBrowser(this.pid)) return;

    void this.router.navigateByUrl('/404', { skipLocationChange: true });
  }
}
